<template>
  <div class="admin-tabs" v-if="tabs.length > 0">
    <div class="tabs-container">
      <div class="tabs-scroll" ref="tabsScrollRef">
        <div class="tabs-list" :style="{ transform: `translateX(${scrollOffset}px)` }">
          <div
            v-for="tab in tabs"
            :key="tab.path"
            class="tab-item"
            :class="{ active: currentPath === tab.path }"
            @click="handleTabClick(tab)"
          >
            <span class="tab-label">{{ tab.title }}</span>
            <var-icon
              v-if="tabs.length > 1"
              name="close"
              size="14"
              class="tab-close"
              @click.stop="handleTabClose(tab)"
            />
          </div>
        </div>
      </div>
      <div class="tabs-actions" v-if="tabs.length > 0">
        <var-button
          text
          round
          size="small"
          @click="scrollTabs(-100)"
          :disabled="scrollOffset >= 0"
        >
          <var-icon name="chevron-left" size="16" />
        </var-button>
        <var-button
          text
          round
          size="small"
          @click="scrollTabs(100)"
          :disabled="!canScrollRight"
        >
          <var-icon name="chevron-right" size="16" />
        </var-button>
        <var-dropdown>
          <var-button text round size="small">
            <var-icon name="dots-vertical" size="16" />
          </var-button>
          <template #menu>
            <var-dropdown-item @click="closeOtherTabs">关闭其他</var-dropdown-item>
            <var-dropdown-item @click="closeAllTabs">关闭全部</var-dropdown-item>
          </template>
        </var-dropdown>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'

const router = useRouter()
const route = useRoute()

const tabs = ref([])
const scrollOffset = ref(0)
const tabsScrollRef = ref(null)
const currentPath = computed(() => route.path)

// 计算是否可以向右滚动
const canScrollRight = computed(() => {
  if (!tabsScrollRef.value) return false
  const containerWidth = tabsScrollRef.value.offsetWidth
  const listWidth = tabsScrollRef.value.scrollWidth
  return Math.abs(scrollOffset.value) + containerWidth < listWidth
})

// 添加标签页（单页模式：不累积，只在当前标签上更新为当前页面）
const addTab = (tab) => {
  const tabInfo = {
    path: tab.path,
    title: tab.title || tab.name || '未命名',
    name: tab.name
  }
  const existingIndex = tabs.value.findIndex(t => t.path === tab.path)
  if (existingIndex >= 0) {
    // 已存在则只更新标题，并移到当前（保持唯一标签时无需移动）
    tabs.value[existingIndex].title = tabInfo.title
  } else {
    // 不增加新标签：用当前页面替换整个标签列表（始终只保留一个标签，在原位跳转）
    tabs.value = [tabInfo]
  }
  scrollToActiveTab()
}

// 移除标签页
const removeTab = (path) => {
  const index = tabs.value.findIndex(t => t.path === path)
  if (index === -1) return

  tabs.value.splice(index, 1)

  // 如果关闭的是当前标签页，跳转到其他标签页
  if (currentPath.value === path && tabs.value.length > 0) {
    const targetTab = tabs.value[index] || tabs.value[index - 1] || tabs.value[0]
    router.push(targetTab.path)
  }

  // 如果所有标签页都关闭了，跳转到仪表盘
  if (tabs.value.length === 0) {
    router.push('/admin/dashboard')
  }
}

// 关闭其他标签页
const closeOtherTabs = () => {
  tabs.value = tabs.value.filter(tab => tab.path === currentPath.value)
}

// 关闭全部标签页
const closeAllTabs = () => {
  tabs.value = []
  router.push('/admin/dashboard')
}

// 处理标签页点击
const handleTabClick = (tab) => {
  router.push(tab.path)
  scrollToActiveTab()
}

// 处理标签页关闭
const handleTabClose = (tab) => {
  removeTab(tab.path)
}

// 滚动标签页
const scrollTabs = (delta) => {
  scrollOffset.value = Math.max(
    -(tabsScrollRef.value?.scrollWidth - tabsScrollRef.value?.offsetWidth || 0),
    Math.min(0, scrollOffset.value + delta)
  )
}

// 滚动到活动标签页
const scrollToActiveTab = async () => {
  await nextTick()
  if (!tabsScrollRef.value) return

  const activeTab = tabsScrollRef.value.querySelector('.tab-item.active')
  if (!activeTab) return

  const containerWidth = tabsScrollRef.value.offsetWidth
  const tabLeft = activeTab.offsetLeft
  const tabWidth = activeTab.offsetWidth
  const tabRight = tabLeft + tabWidth

  // 如果标签页在可视区域外，滚动到合适位置
  if (tabLeft < Math.abs(scrollOffset.value)) {
    scrollOffset.value = -tabLeft + 20
  } else if (tabRight > Math.abs(scrollOffset.value) + containerWidth) {
    scrollOffset.value = -(tabRight - containerWidth) + 20
  }
}

// 监听路由变化，自动添加标签页
watch(() => route.path, (newPath) => {
  if (newPath.startsWith('/admin') && newPath !== '/admin') {
    const title = route.meta?.title || route.name || '未命名'
    addTab({
      path: newPath,
      title,
      name: route.name
    })
  }
}, { immediate: true })

// 暴露方法供父组件调用
defineExpose({
  addTab,
  removeTab,
  closeOtherTabs,
  closeAllTabs
})
</script>

<style scoped>
.admin-tabs {
  height: 40px;
  background: var(--bg-color);
  border-bottom: 1px solid var(--border-color);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  position: relative;
  z-index: 10;
}

.tabs-container {
  display: flex;
  align-items: center;
  height: 100%;
  overflow: hidden;
}

.tabs-scroll {
  flex: 1;
  overflow: hidden;
  position: relative;
}

.tabs-list {
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 8px;
  transition: transform 0.3s ease;
  white-space: nowrap;
}

.tab-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 16px;
  margin-right: 4px;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 6px 6px 0 0;
  cursor: pointer;
  transition: all 0.3s;
  user-select: none;
  min-width: 80px;
  max-width: 200px;
  position: relative;
}

.tab-item:hover {
  background: rgba(255, 255, 255, 0.8);
}

.tab-item.active {
  background: var(--bg-color);
  color: var(--primary-color);
  border-bottom: 2px solid var(--primary-color);
}

.tab-label {
  font-size: 13px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.tab-close {
  opacity: 0.6;
  transition: opacity 0.2s;
  flex-shrink: 0;
}

.tab-item:hover .tab-close {
  opacity: 1;
}

.tab-close:hover {
  color: var(--danger-color, #ef4444);
}

.tabs-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 8px;
  border-left: 1px solid var(--border-color);
  background: var(--bg-color);
}

@media (max-width: 768px) {
  .admin-tabs {
    height: 44px;
  }

  .tab-item {
    padding: 8px 12px;
    min-width: 70px;
    max-width: 150px;
  }

  .tab-label {
    font-size: 12px;
  }

  .tabs-actions {
    padding: 0 4px;
  }
}
</style>